import { Typography } from '@euler/app/components/typography';
import { BlurView } from '@euler/components/blur-view/BlurView';
import { useBoundingRect } from '@euler/functions/ux/useBoundingRect';
import { useLayout } from '@euler/functions/ux/useLayout';
import { usePersistFn } from '@euler/utils/hooks';
import { useLockBodyScroll } from '@uidotdev/usehooks';
import { AnimatePresence, MotiView } from 'moti';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  ListRenderItem,
  Modal,
  Platform,
  StyleSheet,
  TextInputProps,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import DefaultDropdownElement from './element';
import { dropdown } from './icons';
import DropdownMenu from './menu';
import { useSynthesizedDropdownStyles } from './styles';
import {
  DropdownCreatableProps,
  DropdownOption,
  DropdownProps,
  DropdownRenderElementProps,
} from './types';

const kDefaultFilter = <T,>(x: DropdownOption<T>, s: string) =>
  x.label.toLowerCase().includes(s.toLowerCase());

///////////////////////////////////////////////////////////////////////////////
// A pure react native dropdown component with the following built-in features:
// - Validation error message
// - Single and multiple selection
// - Searchable
// - Creatable
// - Clearable
// - Custom item rendering
// - Custom item value rendering
// - Custom list item rendering
// - Custom search filter
// - Custom dropdown content instead of the default menu list
// - Full customizable styles for each part of the dropdown
// - Default or custom position boundary
// - Async loading (TBD)
// - Async creatable (TDB)
// - etc.
///////////////////////////////////////////////////////////////////////////////

/**
 * Dropdown Component Structure
 * ===========================
 *
 * +------------------------------- Dropdown Component ------------------------------------+
 * |                                                                                      |
 * |  +------------------------ Container (TouchableOpacity) ------------------------+     |
 * |  |                                                                              |     |
 * |  |  +--------- Input Container -----------+                                     |     |
 * |  |  |                                     |                                     |     |
 * |  |  |  +--Left--+  +---- Input -------+   |     +--------- Right Icons ----+    |     |
 * |  |  |  |        |  |                  |   |     |                          |    |     |
 * |  |  |  | Icon   |  |                  |   |     |  +---- Clear Icon -----+ |    |     |
 * |  |  |  | (opt)  |  |   Input text     |   |     |  | (when clearable)    | |    |     |
 * |  |  |  |        |  |  or placeholder  |   |     |  +--------------------+ |    |     |
 * |  |  |  +--------+  |                  |   |     |                          |    |     |
 * |  |  |              |                  |   |     |  +---- Caret Icon -----+ |    |     |
 * |  |  |              +------------------+   |     |  |                     | |    |     |
 * |  |  |                                     |     |  +---------------------+ |    |     |
 * |  |  |  +---- Custom Value Wrap ----+      |     |                          |    |     |
 * |  |  |  |   (when renderValue)      |      |     +---------------------------+    |     |
 * |  |  |  |                           |      |                                      |     |
 * |  |  |  +---------------------------+      |                                      |     |
 * |  |  |                                     |                                      |     |
 * |  |  +-------------------------------------+                                      |     |
 * |  |                                                                              |     |
 * |  +------------------------------------------------------------------------------+     |
 * |                                                                                      |
 * |                                                                                      |
 * |  +--------------------------- Dropdown Modal (when open) ------------------------+   |
 * |  |                                                                               |   |
 * |  |  +---------------------------- Backdrop (TouchableWithoutFeedback) ---------+ |   |
 * |  |  |                                                                          | |   |
 * |  |  +--------------------------------------------------------------------------+ |   |
 * |  |                                                                               |   |
 * |  |  +---------------------- Clone of Input (when searchable) -----------------+   |   |
 * |  |  |                                                                         |   |   |
 * |  |  +-------------------------------------------------------------------------+   |   |
 * |  |                                                                               |   |
 * |  |  +-------------------------- Dropdown Container --------------------------+   |   |
 * |  |  |                                                                        |   |   |
 * |  |  |  +---- Dropdown Content ----+                                          |   |   |
 * |  |  |  |                          |                                          |   |   |
 * |  |  |  |  +-- SearchInput (opt) --+                                          |   |   |
 * |  |  |  |  |                       |                                          |   |   |
 * |  |  |  |  +-----------------------+                                          |   |   |
 * |  |  |  |                                                                     |   |   |
 * |  |  |  |  +-- Creatable (opt) ----+                                          |   |   |
 * |  |  |  |  |                       |                                          |   |   |
 * |  |  |  |  +-----------------------+                                          |   |   |
 * |  |  |  |                                                                     |   |   |
 * |  |  |  |  +-- Menu Items ---------+                                          |   |   |
 * |  |  |  |  |  +-----------------+  |                                          |   |   |
 * |  |  |  |  |  | [✓] Menu Item   |  |                                          |   |   |
 * |  |  |  |  |  +-----------------+  |                                          |   |   |
 * |  |  |  |  |  +-----------------+  |                                          |   |   |
 * |  |  |  |  |  | [ ] Menu Item   |  |                                          |   |   |
 * |  |  |  |  |  +-----------------+  |                                          |   |   |
 * |  |  |  |  |  +-----------------+  |                                          |   |   |
 * |  |  |  |  |  | [ ] Menu Item   |  |                                          |   |   |
 * |  |  |  |  |  +-----------------+  |                                          |   |   |
 * |  |  |  |  +-----------------------+                                          |   |   |
 * |  |  |  |                                                                     |   |   |
 * |  |  |  +---------------------------------------------------------------------+   |   |
 * |  |  |                                                                        |   |   |
 * |  |  +------------------------------------------------------------------------+   |   |
 * |  |                                                                               |   |
 * |  +-------------------------------------------------------------------------------+   |
 * |                                                                                      |
 * +--------------------------------------------------------------------------------------+
 *
 *
 * Main Components:
 * ===============
 *
 * 1. Input Container (Visible at all times)
 *    - Left Icon (optional)
 *    - Input field with text/placeholder/error state
 *    - Custom Value Renderer (optional, for custom display of selected value(s))
 *    - Clear Icon (right side, shown when clearable and has value)
 *    - Caret Icon (right side, for dropdown toggle)
 *
 * 2. Dropdown Modal (Visible when open)
 *    - Backdrop (for closing dropdown when clicking outside)
 *    - Clone of Input (when searchable, for inline search)
 *    - Dropdown Container
 *      - Search Input (optional, for filtering options)
 *      - Creatable Section (optional, for adding new items)
 *      - Menu Items List
 *        - Checkboxes (in multiple selection mode)
 *        - Item content (text or custom rendered content)
 *
 * Selection Modes:
 * ===============
 * - Single: Only one item can be selected
 * - Multiple: Multiple items can be selected with checkboxes
 *
 */
function DropdownUnmemo<T>(props: DropdownProps<T>) {
  const {
    filter = kDefaultFilter,
    keyExtractor = defaultKeyExtractor,
    animationDuration = 150,
    renderItem: customRenderItem,
    renderCheck: customRenderCheck,
    onDropdownOpen,
    onDropdownClose,
  } = props;

  const animValue = useSharedValue(0);

  const [open, setOpen] = useState(false);
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [query, setQuery] = useState('');

  const [dropdownLayout, onDropdownLayout, dropdownLayoutChange$] = useLayout();

  const [ref, elemBoundingRect, { onLayout, update }] = useBoundingRect();

  const selectedOption = useMemo(
    () =>
      props.mode !== 'multiple'
        ? props.options?.find(x => x.value === props.value)
        : undefined,
    [props.mode, props.options, props.value],
  );

  const selectedOptions = useMemo(
    () =>
      props.mode === 'multiple'
        ? props.options?.filter(x => props.value?.includes(x.value))
        : undefined,
    [props.mode, props.options, props.value],
  );

  const styles = useSynthesizedDropdownStyles({
    open,
    query,
    autoFlip: props.autoFlip,
    mode: props.mode ?? 'single',
    align: props.align ?? 'left',
    backdropMode: props.backdropMode,
    backdropBlurTint: props.backdropBlurTint,
    backdropBlurIntensity: props.backdropBlurIntensity,
    componentStyles: props.componentStyles,
    placeholderTextColor: props.placeholderTextColor,
    searchInputPlaceholderTextColor: props.searchInputPlaceholderTextColor,
    matchWidth: props.matchWidth,
    maxDropdownHeight: props.maxDropdownHeight,
    showCaretIcon: props.showCaretIcon,
    elemBoundingRect,
    animValue,
    dropdownWidth: dropdownLayout?.width,
    dropdownHeight: dropdownLayout?.height,
    withCustomValueRender: props.renderValue != null && selectedOption != null,
    withClearButton: Boolean(props.clearable && query),
    withIcon: props.icon != null,
  });

  const notifyOpen = usePersistFn(() => {
    onDropdownOpen?.();
  });

  const notifyClose = usePersistFn(() => {
    onDropdownClose?.();
  });

  const onOpen = useCallback(() => {
    update(() => {
      const animate = () => {
        animValue.value = withTiming(1, {
          easing: Easing.inOut(Easing.linear),
          duration: animationDuration,
        });
      };

      setOpen(true);
      setIsBackdropOpen(true);

      notifyOpen();

      if (dropdownLayout == null) {
        const sub = dropdownLayoutChange$.subscribe(() => {
          sub.unsubscribe();
          animate();
        });
      } else {
        animate();
      }
    });
  }, [
    animValue,
    animationDuration,
    dropdownLayout,
    dropdownLayoutChange$,
    notifyOpen,
    update,
  ]);

  const onClose = useCallback(() => {
    setIsBackdropOpen(false);
    animValue.value = withTiming(
      0,
      {
        easing: Easing.inOut(Easing.linear),
        duration: animationDuration,
      },
      () => {
        runOnJS(setOpen)(false);
        runOnJS(notifyClose)();
        if (!interacted) {
          runOnJS(setInteracted)(true);
        }
        if (props.clearSearchInputOnSelect) {
          runOnJS(setQuery)('');
        }
      },
    );
  }, [
    animValue,
    animationDuration,
    interacted,
    notifyClose,
    props.clearSearchInputOnSelect,
  ]);

  const onToggle = useCallback(() => {
    if (open) {
      onClose();
    } else {
      onOpen();
    }
  }, [onClose, onOpen, open]);

  const onOptionItemPress = useCallback(
    (item: DropdownOption<T>) => {
      if (props.mode === 'multiple') {
        const value = props.value ?? [];
        if (value.includes(item.value)) {
          props.onChange?.(value.filter(v => v !== item.value));
          props.onOptionCheckChange?.(item, false);
        } else {
          props.onChange?.([...value, item.value]);
          props.onOptionCheckChange?.(item, true);
        }
      } else {
        props.onChange?.(item.value);
        onClose();
      }
    },
    [onClose, props],
  );

  const onRemoveValue = useCallback(
    (value: T) => {
      if (props.mode === 'multiple' && props.value?.includes(value)) {
        props.onChange?.(props.value.filter(v => v !== value));
      }
    },
    [props],
  );

  const onSearchQueryChange = useCallback(
    (text: string) => {
      setQuery(text);
      if (!open) {
        onOpen();
      }
    },
    [onOpen, open],
  );

  const onClear = useCallback(() => {
    if (props.searchable && props.searchInputPlacement !== 'dropdown-menu') {
      setQuery('');
    } else {
      props.onChange?.(undefined);
    }
    setInteracted(true);
  }, [props]);

  const renderDefaultItemContent = useCallback(
    (item: DropdownOption<T>) => {
      return (
        <Typography.Text style={styles.menuItemText}>
          {item.label}
        </Typography.Text>
      );
    },
    [styles.menuItemText],
  );

  const renderCheck = useCallback(
    (item: DropdownOption<T>) => {
      if (props.mode !== 'multiple') return null;
      const checked = Boolean(props.value?.includes(item.value));
      const style: ViewStyle = { marginRight: 8 };
      const checkIcon = checked ? (
        <dropdown.Checked style={style} />
      ) : (
        <dropdown.Unchecked style={style} />
      );
      return customRenderCheck?.(item, checked, checkIcon) ?? checkIcon;
    },
    [props.mode, props.value, customRenderCheck],
  );

  const renderItem: ListRenderItem<DropdownOption<T>> = useCallback(
    ({ item }) => {
      return (
        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => onOptionItemPress(item)}
        >
          {renderCheck(item)}
          {customRenderItem?.(item, renderDefaultItemContent(item)) ??
            renderDefaultItemContent(item)}
        </TouchableOpacity>
      );
    },
    [
      styles.menuItem,
      renderCheck,
      customRenderItem,
      renderDefaultItemContent,
      onOptionItemPress,
    ],
  );

  const data = useMemo<DropdownOption<T>[]>(() => {
    const s = query.trim();
    return props.options?.filter(x => !x.hidden && (!s || filter(x, s))) ?? [];
  }, [filter, props.options, query]);

  const searchInput = useMemo<TextInputProps | undefined>(() => {
    return props.searchInputPlacement === 'dropdown-menu'
      ? {
          value: query,
          onChangeText: setQuery,
          placeholder: props.searchInputPlaceholder ?? props.placeholder,
          placeholderTextColor:
            props.searchInputPlaceholderTextColor ?? props.placeholderTextColor,
          style: styles.searchInput,
        }
      : undefined;
  }, [
    props.searchInputPlacement,
    props.searchInputPlaceholder,
    props.placeholder,
    props.searchInputPlaceholderTextColor,
    props.placeholderTextColor,
    query,
    styles.searchInput,
  ]);

  const creatableProps = useMemo<DropdownCreatableProps | undefined>(() => {
    if (!props.creatable) return undefined;
    return {
      ...props.creatable,
      input: query,
      style: [styles.creatable, props.creatable.style],
      titleStyle: [styles.creatableTitle, props.creatable.titleStyle],
      inputStyle: [styles.creatableInput, props.creatable.inputStyle],
      onCreate: s => {
        props.creatable?.onCreate?.(s);
        setQuery('');
        onClose();
      },
    };
  }, [
    onClose,
    query,
    props.creatable,
    styles.creatable,
    styles.creatableInput,
    styles.creatableTitle,
  ]);

  const renderElementProps: DropdownRenderElementProps<any> = {
    keyExtractor,
    open,
    mode: props.mode ?? 'single',
    disabled: props.disabled,
    value: props.mode !== 'multiple' ? props.value : undefined,
    values: props.mode === 'multiple' ? props.value : undefined,
    placeholder: props.placeholder,
    placeholderTextColor: styles.placeholderTextColor,
    errorMsg: props.errorMsg,
    loading: props.loading,
    interacted: props.interacted || interacted,
    searchable: props.searchable,
    creatable: props.creatable,
    clearable: props.clearable,
    inputProps: props.inputProps,
    renderValue: props.renderValue,
    renderTag: props.mode === 'multiple' ? props.renderTag : undefined,
    formatTag: props.mode === 'multiple' ? props.formatTag : undefined,
    tagRemovable: props.mode === 'multiple' ? props.tagRemovable : undefined,
    showCaretIcon: props.showCaretIcon,
    formatValue: props.formatValue,
    icon: props.icon,
    componentStyles: styles,
    query,
    animValue,
    renderDefaultItemContent,
    selectedOption,
    selectedOptions,
    onOpen,
    onClose,
    onToggle,
    onClear,
    onRemoveValue,
    onSearchQueryChange,
  };

  const shouldCloneElement =
    ((props.searchable && props.searchInputPlacement !== 'dropdown-menu') ||
      styles.backdropMode !== 'transparent') &&
    props.renderElement == null &&
    open &&
    elemBoundingRect != null;

  const optionKeyExtractor = useCallback(
    (option: DropdownOption<T>) => {
      return keyExtractor(option.value);
    },
    [keyExtractor],
  );

  return (
    <View ref={ref} style={props.style} onLayout={onLayout}>
      {props.renderElement?.(renderElementProps) ?? (
        <DefaultDropdownElement
          editable={false} // always non-editable (non-focusable)
          style={shouldCloneElement ? { opacity: 0 } : undefined}
          withTagsView={
            props.mode === 'multiple' ? props.withTagsView : undefined
          }
          {...renderElementProps}
        />
      )}

      {elemBoundingRect != null && (open || !props.destroyOnHidden) && (
        <Modal
          renderToHardwareTextureAndroid={true}
          transparent={true}
          animationType="none"
          visible={open && elemBoundingRect != null}
          onRequestClose={onClose}
        >
          <TouchableWithoutFeedback onPress={onClose}>
            {styles.backdropMode === 'transparent' ? (
              <View style={[styles.backdrop, StyleSheet.absoluteFill]} />
            ) : (
              <View style={[StyleSheet.absoluteFill]}>
                <AnimatePresence>
                  {isBackdropOpen && (
                    <MotiView
                      style={[
                        styles.backdropMode === 'modal'
                          ? {
                              backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            }
                          : undefined,
                        styles.backdrop,
                        StyleSheet.absoluteFill,
                      ]}
                      from={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        type: 'timing',
                        duration: animationDuration,
                        easing: Easing.inOut(Easing.linear),
                      }}
                      exitTransition={{
                        type: 'timing',
                        duration: animationDuration,
                        easing: Easing.inOut(Easing.linear),
                      }}
                    >
                      {styles.backdropMode === 'blur' && (
                        <BlurView
                          tint={styles.backdropBlurTint}
                          intensity={styles.backdropBlurIntensity}
                          style={StyleSheet.absoluteFill}
                        />
                      )}
                    </MotiView>
                  )}
                </AnimatePresence>
              </View>
            )}
          </TouchableWithoutFeedback>

          {Platform.OS === 'web' && <LockScroll />}

          {/** clone and absolutely position the dropdown element while search */}
          {shouldCloneElement && (
            <DefaultDropdownElement
              {...renderElementProps}
              cloned={true}
              editable={props.searchable ?? false}
              autoFocus={props.searchable ?? false}
              withTagsView={
                props.mode === 'multiple' ? props.withTagsView : undefined
              }
              componentStyles={{
                ...renderElementProps.componentStyles,
                container: [
                  renderElementProps.componentStyles.container,
                  {
                    position: 'absolute',
                    left: elemBoundingRect.x,
                    top: elemBoundingRect.y,
                    width: elemBoundingRect.width,
                    height: elemBoundingRect.height,
                  },
                ],
              }}
            />
          )}

          <Animated.View
            style={[styles.dropdownContainer, styles.animatedDropdownStyle]}
          >
            {props.children ? (
              <View onLayout={onDropdownLayout} style={styles.dropdown}>
                {typeof props.children === 'function'
                  ? props.children({ onClose })
                  : props.children}
              </View>
            ) : (
              <DropdownMenu
                type="list"
                data={data}
                renderItem={renderItem}
                ItemSeparatorComponent={props.ItemSeparatorComponent}
                keyExtractor={optionKeyExtractor}
                stickySearchInput={props.stickySearchInputInDropdown}
                searchInput={searchInput}
                creatable={creatableProps}
                style={styles.dropdown}
                contentContainerStyle={[styles.dropdownContentContainer]}
                onLayout={onDropdownLayout}
              />
            )}
          </Animated.View>
        </Modal>
      )}
    </View>
  );
}

export const Dropdown = memo(DropdownUnmemo) as typeof DropdownUnmemo;

const LockScroll = memo(() => {
  useLockBodyScroll();
  return null;
});

function defaultKeyExtractor(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null) {
    return 'null';
  }

  if (value === undefined) {
    return 'undefined';
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    typeof value === 'symbol'
  ) {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  throw new Error(
    `Unsupported value type: ${Object.prototype.toString.call(
      value,
    )}, key extractor is required`,
  );
}
